/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Mobx model(s) related to product
 *
 * @file Product.js
 * @author sandeep and Arish Kumar K
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import { EventProvider } from '@aquila/core';

import Subscription from './Subscription';
import Configuration from './Configuration';
import env from '../../../common/env';
import PathUtils from '../../../common/utils/PathUtils';
import ProductUtils from '../../../common/utils/ProductUtils';
import CommonUtils from '../../../common/utils/CommonUtils';
import * as constants from '../../../common/constants';
import canadaBookList from '../../../common/canadaBookList.json';

const DEFAULT_CONFIG = {
  allowNotesCopy: false,
  allowTocEdit: false,
  applicationId: 'ise',
  enableQuickView: false,
  showAudio: true,
  showAudioBeta: false,
  showBackNav: true,
  showBookmark: true,
  showDrawer: true,
  showDropMenu: false,
  showHeader: true,
  showLastVisitedPage: true,
  showPref: true,
  showResource: false,
  showSearch: true,
  showShareNotes: false,
  showStudy: true,
  tenantId: env.TENANT_ID_ETEXT,
  tenantKey: env.TENANT_KEY_ETEXT,
  showSocialComponent: false,
  showPrintPageNav: false,
  showABSearch: false,
  showCopy: false,
  showAudioReadAlong: false,
  showABNavigation: false,
  showABAudio: false,
  showStudyPanelChannelsWidget: false,
  enableUserLastLocation: false,
  showABNavIcons: false,
  showFlashcardViewInContent: false,
  showFooter: true,
  showStudyChannelNewVariation: false,
  updateLastLocation: true,
  showABChannelsIconPosition: false,
  showABAITutor: true,
  showAITutor: false,
  enableAIShowMore: false,
  isFirstTimeAITutorUser: null,
  enableChannelInSearch: false,
  enableAISleepMode: true,
  showAISleepModeAB: true,
  showAIChannelVideos: false,
  enableTopicsForChannel: false,
  isLDFlagsFetched: false,
  enableDigitalOrPrintPageNavigation: false,
  enableSummaryContinuation: false,
  enableFooterAudio: false,
  miniPlayerHeaderABTestFeature: 0,
  enableAIForGlossary: false,
  enableAIShortAnswer: false,
  enableProblemSolveFeature: false,
  showSearchChannelVideoChanges: false,
  enableTOCPrintPage: false,
  enableExplainQuizOptions: 1,
  AIChatPlaceHolderText: '',
  enableTopicInQuizFlow: '',
  AIStudyToolAutoOpenLimit: 0,
  aitutorWelcomeScreenCohorts: constants.CRUSH_YOUR_FINALS,
  aiTutorAutoOpenCommands: null,
  aiTutorAutoOpenCount: 0,
  miniPlayerPaywallTestFeature: 0,
  showSearchChannelVideoPlayer: true,
  showUserHistory: false,
  enableMathMLKeyboard: false,
  isBundleSubscriber: null,
  isChannelsSubscriber: null,
  enableBundleChannelMiniPlayer: false,
  ChannelsVideoScore: 10,
  promptShownCount: 0,
  starterPromptLimit: 0,
  hideChannelsPopout: false,
  showTranslate: false,
  translationLanguage: null,
  showRecommendedPrompts: false,
  showSourceReferencePage: false,
  AIEnableMarkdown: false,
  AIEnableImageContext: false,
  isSearchStudyAutoSelected: false,
  showSummary: true,
  showPractice: true,
  enableNewMathConfig: false,
  enableAISearch: false,
  isAIContentMediaIcon: false,
  aiStudyToolNameABTest: false,
  AIStudyToolReminder: 0,
  showChannelsSubscribersStickyBanner: true,
  AIEnableNewValidateAnswer: false,
  channelRecommendationRedirectionABTest: 0,
  searchChannelRecommendedMessage: 1,
  processMarkDownFirst: false,
  shareChatbotReference: false,
  glossaryABCTest: 'A',
  AIEnableHigherOrderSuggestion: false,
  AIEnableQuizRandomization: false,
  enableChannelsRecommendations: true,
  searchChannelRecommendationDelay: 5,
  quizQuestionLimit: 10,
  miniplayerNoMatchingAssetsABTest: 0,
  showRedirectionOverlay: false,
  enableAgenticAI: false,
  agenticGraphConfig: constants.AGENTIC_GRAPH_CONFIG,
  searchOverviewGraphConfig: constants.AGENTIC_GRAPH_CONFIG,
  enableAgenticSearchAIOverview: false,
  enableRTAudioTranslation: false,
  audioTranslationLanguage: null,
  audioLanguage: null,
  AIEnableQuizOptionShuffle: false,
  isAETEnabled: false,
  AIEnableDynamicSummary: false,
  AIEnableDynamicQuiz: false,
  AIShowFooterFAQLink: false
};
const DEFAULT_MLM_CONFIG = {
  showHighlights: false,
  showBookmark: false,
  showShareNote: false,
  allowNotesCopy: false,
  showDrawer: false,
  showHeader: false,
  showStudy: false,
  showSearch: false,
  showFooter: false,
  showPref: false,
  updateLastLocation: false,
  showABChannelsIconPosition: false,
  showQuizProgress: false,
  enableAET: false,
  assignmentCompletionPopupABTest: 0,
  showAeTAssignmentsABCTest: 1,
  enableUnassignedAETForInstructor: false
};
const DEFAULT_PDF_CONFIG = {
  mathmlOn: false,
  enableQuickView: false,
  showAudio: false,
  showAudioBeta: false,
  showStudy: true,
  applicationId: 'etext1',
  showSocialComponent: false,
  showABSearch: false,
  showCopy: false,
  showABChannelsIconPosition: false
};

/**
 * A mobx model for author
 *
 */
const Author = types.model(
  'Author',
  {
    firstName: types.maybeNull(types.string),
    lastName: types.maybeNull(types.string),
    otherName: types.maybeNull(types.string)
  }
);

/**
 * A mobx model for Secondary channels
 *
 */
export const secondaryChannels = types.model(
  'SecondaryChannels',
  {
    channel_name: types.maybeNull(types.string),
    channel_id: types.maybeNull(types.string)
  }
);

/**
 * A mobx model for product
 *
 */
const Product = types.model(
  'Product',
  {
    audioBook: types.optional(types.string, ''),
    authors: types.maybeNull(types.union(types.array(Author), types.string)),
    basePath: types.maybeNull(types.string),
    contentBasePath: types.maybeNull(types.string),
    backLinkUrl: types.maybeNull(types.string),
    bookID: types.maybeNull(types.union(types.string, types.integer, types.number)),
    catalogUrl: types.maybeNull(types.string),
    configuration: types.optional(Configuration, {
      enableUserPrinting: false,
      studyTools: false,
      printOfferUrl: '',
      includeMathMLLib: false,
      headerFeatures: {
        hasPrintLink: false
      },
      generalFeatures: {
        hasLeftAccordion: false
      },
      ipadFeatures: {
        hotSpotColor: '',
        isUnderLineHotspot: false,
        regionHotSpotAlpha: '',
        underLineHotSpotColor: '',
        underLineHotSpotThickness: '',
        underLineHotSpotHoverColor: ''
      },
      availableBookLanguages: [{
        languageDirection: '',
        languageID: 1,
        languageNameKey: ''
      }]
    }),
    coverThumbnailUrl: types.maybeNull(types.string),
    format: types.maybeNull(types.string),
    id: types.string,
    indexId: types.maybe(types.string),
    mathmlOn: types.optional(types.boolean, false),
    mobileReady: types.optional(types.boolean, false),
    model: types.maybeNull(types.string),
    platform: types.maybeNull(types.string),
    role: types.optional(types.string, 'student'),
    read_along: types.optional(types.boolean, false),
    status: types.maybeNull(types.string),
    thumbnailUrl: types.maybeNull(types.string),
    title: types.maybeNull(types.string),
    version: types.maybeNull(types.string),
    subscription: types.optional(Subscription, {
      allowAccess: false,
      businessModelCode: '',
      entitlementTier: null,
      entitlementLevel: null,
      endDate: 0,
      startDate: 0,
      startDateUTC: null,
      status: null,
      type: '',
      subscriptionId: ''
    }),
    tocUrl: types.maybeNull(types.string),
    serversideProcessed: types.maybeNull(types.union(types.string, types.integer, types.boolean)),
    serversideEnabled: types.maybeNull(types.union(types.string, types.integer, types.boolean)),
    serverSideUuid: types.maybeNull(types.string),
    twoPageLayoutLeft: types.maybeNull(types.string),
    social_supported: types.maybeNull(types.boolean),
    legacyBookId: types.maybeNull(types.string),
    legacyProductId: types.maybeNull(types.string),
    channel_name: types.maybeNull(types.string),
    channel_id: types.maybeNull(types.string),
    channel_mapping_type: types.maybeNull(types.string),
    hasAccessToExactOrLiteMatch: types.optional(types.boolean, false),
    secondary_channels: types.maybeNull(types.array(secondaryChannels)),
    insitu_videos: types.maybeNull(types.boolean),
    isPPlusProduct: types.optional(types.boolean, false),
    isAITutorSupported: types.maybeNull(types.string),
    isAIChannelTopicsSupported: types.maybeNull(types.string),
    isAETEnabled: types.maybeNull(types.boolean),
    isDynamicQuizSupported: types.maybeNull(types.boolean),
    isDynamicSummarySupported: types.maybeNull(types.boolean)
  }
).views(self => ({
  getBasePath(isBasePathChange, serverSideEnabled) {
    if (self.format === constants.CONTENT_TYPE_CITE) {
      return `${env.CONTENT_HOST}/${env.CITE_BASE_PATH}/${self.basePath}`;
    }
    if (self.format === constants.CONTENT_TYPE_PXE || self.model === constants.CUSTOM_COLLECTIONS) {
      return PathUtils.getAbsolutePath(self.contentBasePath);
    }
    if (self.model === constants.EPUB_BRONTE) {
      return `${env.CONTENT_HOST}/${env.EPUB_BASE_PATH}/${self.basePath}/`;
    }
    if (self.model === constants.EPUB_BRONTE_STANDARD) {
      return `${env.CONTENT_HOST}/${env.EPUB_STD_BASE_PATH}/${self.basePath}/`;
    }
    if (self.model === constants.ETEXT_PDF && !serverSideEnabled) {
      const s3Env = (self.basePath).split('/')[3];
      return isBasePathChange ? `${env.CONTENT_HOST}/etext/pdfassets/${s3Env}/ebookassets/ebook${self.indexId}/ipadpdfs/` : `${env.CONTENT_HOST}/eplayer/ebookassets/${s3Env}/ebook${self.indexId}/`;
    }
    if (self.model === constants.ETEXT_BV_BRONTE) {
      return `${env.CONTENT_HOST}/${env.BRONTE_BASE_PATH}/${self.basePath}`;
    }

    return `${env.CONTENT_HOST}${self.basePath}/`;
  },
  getConfig(locale, bundleId, featureFlags = false, userPreferences = false, isIntergratedMLMLaunch = false, isLDFlagsFetched = false, extraConfig = {}) {
    const { 
      scrubberFeature,
      studyGuide,
      enablePrintPageNavigation,
      showABSearch,
      enableCopy,
      enableAudioReadAlong,
      showABNavigation,
      showABAudio,
      enableUserLastLocation,
      showABNavIcons,
      showFlashcardViewInContent,
      enablePrintPageNavigationBronte,
      showPDFChapterFilter,
      showABChannelsIconPosition,
      showABAITutor,
      showAITutor,
      topicTimeOut,
      enableStreamingAPI,
      delayMessageTimerInSecs,
      enableFreeResponse,
      enableParagraph,
      enableAIShowMore,
      enableChannelInSearch,
      enableAISleepMode,
      showAISleepModeAB,
      showAIChannelVideos,
      isAIReviewUser,
      enableTopicsForChannel,
      enableDigitalOrPrintPageNavigation,
      enableSummaryContinuation,
      enableFooterAudio,
      miniPlayerHeaderABTestFeature,
      enableAIForGlossary,
      enableAIShortAnswer: listOfBookAIShortAnswer,
      enableProblemSolveFeature,
      showSearchChannelVideoChanges,
      enableTOCPrintPage,
      enableExplainQuizOptions,
      AIChatPlaceHolderText,
      enableTopicInQuizFlow,
      AIStudyToolAutoOpenLimit,
      aitutorWelcomeScreenCohorts,
      aiTutorAutoOpenCommands,
      miniPlayerPaywallTestFeature,
      showSearchChannelVideoPlayer,
      showUserHistory,
      enableMathMLKeyboard,
      enableBundleChannelMiniPlayer,
      ChannelsVideoScore,
      starterPromptLimit,
      hideChannelsPopout,
      showTranslate: enableTranslate,
      disableTranslateTitles,
      showRecommendedPrompts,
      showSourceReferencePage,
      AIEnableMarkdown,
      AIEnableImageContext,
      isSearchStudyAutoSelected,
      AIBerlinTurnOffSummary,
      AIBerlinTurnOffPractice,
      enableAISearch,
      AIBotIconNextToContentImage,
      AIStudyToolReminder,
      channelRecommendationRedirectionABTest,
      searchChannelRecommendedMessage,
      glossaryABCTest,
      enableChannelsRecommendations,
      searchChannelRecommendationDelay,
      miniplayerNoMatchingAssetsABTest,
      showRedirectionOverlay,
      enableRTAudioTranslate,
      enableRTAudioTranslateTitles,
      showQuizProgress,
      enableAET,
      assignmentCompletionPopupABTest,
      showAeTAssignmentsABCTest,
      enableUnassignedAETForInstructor,
      searchOverviewGraphConfig,
      enableAgenticSearchAIOverview
    } = featureFlags;

    const {
      isTextCopyEnabled,
      isFirstTimeAITutorUser,
      aiTutorAutoOpenCount,
      promptShownCount,
      translationLanguage,
      showChannelsSubscribersStickyBanner,
      audioTranslationLanguage
    } = userPreferences;

    const { showBackNav } = CommonUtils.showBackNav(extraConfig);
    const {
      isStudyPrepEnabled,
      isUnassignedMCQEnabled,
      isAIStudyEnabled,
      courseId
    } = extraConfig;

    const isBundleSubscriber = isLDFlagsFetched ? CommonUtils.isBundleSubscriber() : null;
    const isChannelsSubscriber = isLDFlagsFetched ? self?.hasAccessToExactOrLiteMatch : null;
    const primaryChannelId = this.getPrimaryChannelId();

    const isCITE = self.format === constants.CONTENT_TYPE_CITE;
    const isPXE = self.format === constants.CONTENT_TYPE_PXE;
    const isBronte = self.format === constants.CONTENT_TYPE_EPUB;
    const isBVBronte = ProductUtils.isBVBronteBook(self.format);
    const isSPdf = self.getServerSideStatus();
    // CITE type disable the DigitalOrPrintPageNavigation
    const isPrintPageNumberNavigation = isCITE ? false : enableDigitalOrPrintPageNavigation;
    const { currentLanguage } = Framework.getStoreRegistry().getStore('language');
    const isPdf = self.model === constants.ETEXT_PDF;
    const userLastLocationEnabled = ((isCITE || isPXE || isBronte) && enableUserLastLocation);
    const isshowFlashcardViewInContentEnabled = (isPXE || isBronte || isCITE || isBVBronte) ? showFlashcardViewInContent : false;
    const isPrintPageNavigationEnabled = isBronte ? enablePrintPageNavigationBronte : enablePrintPageNavigation;
    const isStudyChannelNewVariation = self.insitu_videos;
    const pdfChapterFilter = (isSPdf || isPdf) ? showPDFChapterFilter : true;
    const channelName = self.channel_name;
    const showStudyPanelChannelsWidget = CommonUtils.shouldEnableChannelMiniPlayer({
      primaryChannelId,
      channelName,
      featureFlags,
      isStudyPrepEnabled,
      channelAccess: CommonUtils.getChannelAccess(self, courseId)
    });
    const isShowAITutorEnabled = CommonUtils.isAITutorEnabled(showAITutor, isAIReviewUser, self.isAITutorSupported, isAIStudyEnabled);
    const {
      enableNewMathConfig,
      aiStudyToolNameABTest,
      AIEnableNewValidateAnswer,
      processMarkDownFirst,
      shareChatbotReference,
      AIEnableHigherOrderSuggestion,
      AIEnableQuizRandomization,
      quizQuestionLimit,
      enableAgenticAI,
      agenticGraphConfig,
      AIEnableQuizOptionShuffle,
      AIEnableDynamicSummary,
      AIEnableDynamicQuiz,
      AIShowFooterFAQLink
    } = CommonUtils.commonAIConfig(featureFlags, self, {
      isAITutorEnabled: isShowAITutorEnabled,
      bookId: self.bookID,
      productId: self.id
    });
    // TODO: need to add common function to get the bookId/PoductId
    const showSummary = Array.isArray(AIBerlinTurnOffSummary) ? !AIBerlinTurnOffSummary.includes((self.bookID || self.id)) : true;
    const showPractice = Array.isArray(AIBerlinTurnOffPractice) ? !AIBerlinTurnOffPractice.includes((self.bookID || self.id)) : true;
    // Enable AI channel videos for exact channel mapped title
    const enableAIChannelVideos = showAIChannelVideos && !!primaryChannelId && !CommonUtils.localStringCompare(
      self.channel_mapping_type, constants.CHANNEL_MAPPING_TYPES.EXACTLITE);
    const isAIContentMediaIcon = AIEnableImageContext && CommonUtils.isTitleInList(
      AIBotIconNextToContentImage?.titles, { bookId: self.bookID, productId: self.id }
    );
    let isTopicInQuizFlow = false;
    if (enableTopicInQuizFlow) {
      const listOfBookIdsTopicInQuizFlow = enableTopicInQuizFlow.split('_');
      isTopicInQuizFlow = listOfBookIdsTopicInQuizFlow.includes(self.bookID) || listOfBookIdsTopicInQuizFlow.includes(self.id);
    }

    let isEnableTopicsForChannel = false;

    if (primaryChannelId) {
      if (self.isAIChannelTopicsSupported === constants.LIVE) {
        isEnableTopicsForChannel = enableTopicsForChannel;
      } else if (self.isAIChannelTopicsSupported === constants.REVIEW) {
        isEnableTopicsForChannel = isAIReviewUser;
      }
    }

    let enableAIShortAnswer = false;
    if (!CommonUtils.isEmpty(listOfBookAIShortAnswer) && listOfBookAIShortAnswer.titles && listOfBookAIShortAnswer.titles.length > 0) {
      enableAIShortAnswer = listOfBookAIShortAnswer.titles.some(({ bookId, productId }) => (self.bookID === bookId || self.id === productId));
    }
    const showTranslate = enableTranslate && !CommonUtils.isTitleInList(
      disableTranslateTitles?.titles, { bookId: self.bookID, productId: self.id }
    );
    const isNotPdfTypeBook = !self.isPdf();
    const enableRTAudioTranslation = isNotPdfTypeBook && enableRTAudioTranslate && CommonUtils.isTitleInObjectList(
      enableRTAudioTranslateTitles, { bookId: self.bookID, productId: self.id }
    );

    // For RT testing forceFully enable the audio for specfic title
    // TODO: Need to be cleanup after testing success
    let forceFullEnableAudio = false;
    let audioLanguage = null;
    if (enableRTAudioTranslation) {
      const { enableAudio = false, audioLanguage: audioLang = null } = CommonUtils.getTitleInList(
        enableRTAudioTranslateTitles?.titles, { bookId: self.bookID, productId: self.id }
      );
      forceFullEnableAudio = enableAudio;
      audioLanguage = audioLang;
    }
    const showAudio = self.isAudioBook(self.audioBook) || forceFullEnableAudio;

    if (isIntergratedMLMLaunch) {
      return {
        ...DEFAULT_CONFIG,
        ...DEFAULT_MLM_CONFIG
      };
    }
    if (bundleId) {
      return {
        ...DEFAULT_CONFIG,
        mathmlOn: self.mathmlOn || self.configuration.includeMathMLLib,
        showDrawer: true,
        showPref: true,
        showBackNav: false,
        showSearch: true,
        showDropMenu: false,
        showLastVisitedPage: false,
        showAudio: false,
        showBookmark: false,
        showResource: false,
        enableQuickView: false,
        showShareNotes: false,
        allowTocEdit: false,
        allowNotesCopy: false,
        locale: currentLanguage,
        showToc: false,
        showStudy: false,
        showNavigation: false,
        showHighlights: false,
        showPrintPageNav: isPrintPageNavigationEnabled,
        showABSearch,
        showCopy: enableCopy && isTextCopyEnabled,
        showAudioReadAlong: ((enableAudioReadAlong) && (self.read_along) && (isPXE || isBronte || isCITE || isBVBronte)),
        showABNavigation,
        showABAudio,
        showStudyPanelChannelsWidget,
        enableUserLastLocation: userLastLocationEnabled,
        showABNavIcons,
        showFlashcardViewInContent: isshowFlashcardViewInContentEnabled,
        showStudyChannelNewVariation: isStudyChannelNewVariation,
        showPDFChapterFilter: pdfChapterFilter,
        showABChannelsIconPosition,
        showABAITutor,
        showAITutor: isShowAITutorEnabled,
        enableAIShowMore,
        isFirstTimeAITutorUser,
        topicTimeOut,
        enableStreamingAPI,
        delayMessageTimerInSecs,
        enableFreeResponse,
        enableParagraph,
        enableChannelInSearch,
        enableAISleepMode,
        showAISleepModeAB,
        showAIChannelVideos: enableAIChannelVideos,
        enableTopicsForChannel: isEnableTopicsForChannel,
        isLDFlagsFetched,
        enableDigitalOrPrintPageNavigation: isPrintPageNumberNavigation,
        enableSummaryContinuation,
        enableFooterAudio,
        miniPlayerHeaderABTestFeature,
        enableAIForGlossary,
        enableAIShortAnswer,
        enableProblemSolveFeature,
        showSearchChannelVideoChanges,
        enableTOCPrintPage,
        enableExplainQuizOptions,
        AIChatPlaceHolderText,
        enableTopicInQuizFlow: isTopicInQuizFlow,
        AIStudyToolAutoOpenLimit,
        aitutorWelcomeScreenCohorts,
        aiTutorAutoOpenCommands,
        aiTutorAutoOpenCount,
        miniPlayerPaywallTestFeature,
        showSearchChannelVideoPlayer,
        showUserHistory,
        enableMathMLKeyboard,
        isBundleSubscriber,
        isChannelsSubscriber,
        enableBundleChannelMiniPlayer,
        ChannelsVideoScore,
        promptShownCount,
        starterPromptLimit,
        hideChannelsPopout,
        showTranslate,
        translationLanguage,
        showRecommendedPrompts,
        showSourceReferencePage,
        AIEnableMarkdown,
        AIEnableImageContext,
        isSearchStudyAutoSelected,
        showSummary,
        showPractice,
        enableNewMathConfig,
        enableAISearch,
        isAIContentMediaIcon,
        aiStudyToolNameABTest,
        AIStudyToolReminder,
        showChannelsSubscribersStickyBanner,
        AIEnableNewValidateAnswer,
        channelRecommendationRedirectionABTest,
        searchChannelRecommendedMessage,
        processMarkDownFirst,
        shareChatbotReference,
        glossaryABCTest,
        AIEnableHigherOrderSuggestion,
        AIEnableQuizRandomization,
        enableChannelsRecommendations,
        searchChannelRecommendationDelay,
        quizQuestionLimit,
        miniplayerNoMatchingAssetsABTest,
        showRedirectionOverlay,
        enableAgenticAI,
        agenticGraphConfig,
        searchOverviewGraphConfig,
        enableAgenticSearchAIOverview,
        enableRTAudioTranslation,
        audioTranslationLanguage,
        audioLanguage,
        AIEnableQuizOptionShuffle,
        showQuizProgress,
        enableAET,
        assignmentCompletionPopupABTest,
        showAeTAssignmentsABCTest,
        enableUnassignedAETForInstructor,
        AIEnableDynamicSummary,
        AIEnableDynamicQuiz,
        AIShowFooterFAQLink
      };
    }
    if (self.format === constants.CONTENT_TYPE_PDF) {
      if (isPdf) {
        const isCanadianConfig = self.isCanadaBook(self.bookID);

        return {
          ...DEFAULT_CONFIG,
          ...DEFAULT_PDF_CONFIG,
          showAudio,
          showAudioBeta: self.isAudioBetaBook(self.audioBook),
          showBackNav,
          showBookmark: isCanadianConfig ? false : self.configuration.toolBarFeatures.hasBookMarkPageButton,
          showDrawer: self.configuration.generalFeatures.hasLeftAccordion,
          tenantKey: env.TENANT_KEY_ETEXT_PDF,
          clientApp: constants.PDF_CLIENT_APP,
          locale,
          showHighlights: !isCanadianConfig,
          showNavigation: scrubberFeature,
          showStudy: studyGuide && !isCanadianConfig,
          showPrintPageNav: isPrintPageNavigationEnabled,
          showABSearch,
          showCopy: enableCopy && isTextCopyEnabled,
          showABNavigation,
          showABAudio,
          showABNavIcons,
          showStudyChannelNewVariation: isStudyChannelNewVariation,
          showPDFChapterFilter: pdfChapterFilter,
          showStudyPanelChannelsWidget,
          showABChannelsIconPosition,
          showABAITutor,
          showAITutor: isShowAITutorEnabled,
          enableAIShowMore,
          isFirstTimeAITutorUser,
          topicTimeOut,
          enableStreamingAPI,
          delayMessageTimerInSecs,
          enableFreeResponse,
          enableParagraph,
          enableChannelInSearch,
          enableAISleepMode,
          showAISleepModeAB,
          enableTopicsForChannel: isEnableTopicsForChannel,
          isLDFlagsFetched,
          enableDigitalOrPrintPageNavigation: isPrintPageNumberNavigation,
          enableSummaryContinuation,
          enableFooterAudio,
          miniPlayerHeaderABTestFeature,
          enableAIForGlossary,
          enableAIShortAnswer,
          enableProblemSolveFeature,
          showSearchChannelVideoChanges,
          enableTOCPrintPage,
          enableExplainQuizOptions,
          AIChatPlaceHolderText,
          enableTopicInQuizFlow: isTopicInQuizFlow,
          AIStudyToolAutoOpenLimit,
          aitutorWelcomeScreenCohorts,
          aiTutorAutoOpenCommands,
          aiTutorAutoOpenCount,
          miniPlayerPaywallTestFeature,
          showSearchChannelVideoPlayer,
          showUserHistory,
          enableMathMLKeyboard,
          isBundleSubscriber,
          isChannelsSubscriber,
          enableBundleChannelMiniPlayer,
          ChannelsVideoScore,
          promptShownCount,
          starterPromptLimit,
          hideChannelsPopout,
          showTranslate,
          translationLanguage,
          showRecommendedPrompts,
          showSourceReferencePage,
          AIEnableMarkdown,
          AIEnableImageContext,
          isSearchStudyAutoSelected,
          showSummary,
          showPractice,
          enableNewMathConfig,
          enableAISearch,
          isAIContentMediaIcon,
          aiStudyToolNameABTest,
          AIStudyToolReminder,
          showChannelsSubscribersStickyBanner,
          AIEnableNewValidateAnswer,
          channelRecommendationRedirectionABTest,
          searchChannelRecommendedMessage,
          processMarkDownFirst,
          shareChatbotReference,
          glossaryABCTest,
          AIEnableHigherOrderSuggestion,
          AIEnableQuizRandomization,
          enableChannelsRecommendations,
          searchChannelRecommendationDelay,
          quizQuestionLimit,
          miniplayerNoMatchingAssetsABTest,
          showRedirectionOverlay,
          enableAgenticAI,
          agenticGraphConfig,
          searchOverviewGraphConfig,
          enableAgenticSearchAIOverview,
          enableRTAudioTranslation,
          audioTranslationLanguage,
          audioLanguage,
          AIEnableQuizOptionShuffle,
          showQuizProgress,
          enableAET,
          assignmentCompletionPopupABTest,
          showAeTAssignmentsABCTest,
          enableUnassignedAETForInstructor,
          AIEnableDynamicSummary,
          AIEnableDynamicQuiz,
          AIShowFooterFAQLink
        };
      }
      if (self.model === constants.CUSTOM_COLLECTIONS) {
        return {
          ...DEFAULT_CONFIG,
          clientApp: constants.COLLECTION_CLIENT_APP,
          showStudy: studyGuide,
          showNavigation: scrubberFeature,
          showAudio,
          showAudioBeta: self.isAudioBetaBook(self.audioBook),
          showPrintPageNav: isPrintPageNavigationEnabled,
          showABSearch,
          showCopy: enableCopy && isTextCopyEnabled,
          showABNavigation,
          showABAudio,
          showABNavIcons,
          showStudyChannelNewVariation: isStudyChannelNewVariation,
          showPDFChapterFilter: pdfChapterFilter,
          showStudyPanelChannelsWidget,
          showABChannelsIconPosition,
          showABAITutor,
          showAITutor: isShowAITutorEnabled,
          enableAIShowMore,
          isFirstTimeAITutorUser,
          topicTimeOut,
          enableStreamingAPI,
          delayMessageTimerInSecs,
          enableFreeResponse,
          enableParagraph,
          enableChannelInSearch,
          enableAISleepMode,
          showAISleepModeAB,
          showAIChannelVideos: enableAIChannelVideos,
          enableTopicsForChannel: isEnableTopicsForChannel,
          isLDFlagsFetched,
          enableDigitalOrPrintPageNavigation: isPrintPageNumberNavigation,
          enableSummaryContinuation,
          enableFooterAudio,
          miniPlayerHeaderABTestFeature,
          enableAIForGlossary,
          enableAIShortAnswer,
          enableProblemSolveFeature,
          showSearchChannelVideoChanges,
          enableTOCPrintPage,
          enableExplainQuizOptions,
          AIChatPlaceHolderText,
          enableTopicInQuizFlow: isTopicInQuizFlow,
          AIStudyToolAutoOpenLimit,
          aitutorWelcomeScreenCohorts,
          aiTutorAutoOpenCommands,
          aiTutorAutoOpenCount,
          miniPlayerPaywallTestFeature,
          showSearchChannelVideoPlayer,
          showUserHistory,
          enableMathMLKeyboard,
          isBundleSubscriber,
          isChannelsSubscriber,
          enableBundleChannelMiniPlayer,
          ChannelsVideoScore,
          promptShownCount,
          starterPromptLimit,
          hideChannelsPopout,
          showTranslate,
          translationLanguage,
          showRecommendedPrompts,
          showSourceReferencePage,
          AIEnableMarkdown,
          AIEnableImageContext,
          isSearchStudyAutoSelected,
          showSummary,
          showPractice,
          enableNewMathConfig,
          enableAISearch,
          isAIContentMediaIcon,
          aiStudyToolNameABTest,
          AIStudyToolReminder,
          showChannelsSubscribersStickyBanner,
          AIEnableNewValidateAnswer,
          channelRecommendationRedirectionABTest,
          searchChannelRecommendedMessage,
          processMarkDownFirst,
          shareChatbotReference,
          glossaryABCTest,
          AIEnableHigherOrderSuggestion,
          AIEnableQuizRandomization,
          enableChannelsRecommendations,
          searchChannelRecommendationDelay,
          quizQuestionLimit,
          miniplayerNoMatchingAssetsABTest,
          showRedirectionOverlay,
          showBackNav,
          enableAgenticAI,
          agenticGraphConfig,
          searchOverviewGraphConfig,
          enableAgenticSearchAIOverview,
          enableRTAudioTranslation,
          audioTranslationLanguage,
          audioLanguage,
          AIEnableQuizOptionShuffle,
          showQuizProgress,
          enableAET,
          assignmentCompletionPopupABTest,
          showAeTAssignmentsABCTest,
          enableUnassignedAETForInstructor,
          AIEnableDynamicSummary,
          AIEnableDynamicQuiz,
          AIShowFooterFAQLink
        };
      }
      return {
        ...DEFAULT_CONFIG,
        ...DEFAULT_PDF_CONFIG
      };
    }
    return {
      ...DEFAULT_CONFIG,
      mathmlOn: self.mathmlOn || self.configuration.includeMathMLLib,
      indexId: self.indexId,
      showBackNav,
      showStudy: studyGuide,
      locale: currentLanguage,
      showAudio,
      showAudioBeta: self.isAudioBetaBook(self.audioBook),
      showNavigation: scrubberFeature,
      showPrintPageNav: isPrintPageNavigationEnabled,
      showABSearch,
      showCopy: enableCopy && isTextCopyEnabled,
      showAudioReadAlong: ((enableAudioReadAlong) && (self.read_along) && (isPXE || isBronte || isCITE || isBVBronte)),
      showABNavigation,
      showABAudio,
      showStudyPanelChannelsWidget,
      enableUserLastLocation: userLastLocationEnabled,
      showABNavIcons,
      showFlashcardViewInContent: isshowFlashcardViewInContentEnabled,
      showStudyChannelNewVariation: isStudyChannelNewVariation,
      showPDFChapterFilter: pdfChapterFilter,
      showABChannelsIconPosition,
      showABAITutor,
      showAITutor: isShowAITutorEnabled,
      enableAIShowMore,
      isFirstTimeAITutorUser,
      topicTimeOut,
      enableStreamingAPI,
      delayMessageTimerInSecs,
      enableFreeResponse,
      enableParagraph,
      enableChannelInSearch,
      enableAISleepMode,
      showAISleepModeAB,
      showAIChannelVideos: enableAIChannelVideos,
      enableTopicsForChannel: isEnableTopicsForChannel,
      isLDFlagsFetched,
      enableDigitalOrPrintPageNavigation: isPrintPageNumberNavigation,
      enableSummaryContinuation,
      enableFooterAudio,
      miniPlayerHeaderABTestFeature,
      enableAIForGlossary,
      enableAIShortAnswer,
      enableProblemSolveFeature,
      showSearchChannelVideoChanges,
      enableTOCPrintPage,
      enableExplainQuizOptions,
      AIChatPlaceHolderText,
      enableTopicInQuizFlow: isTopicInQuizFlow,
      AIStudyToolAutoOpenLimit,
      aitutorWelcomeScreenCohorts,
      aiTutorAutoOpenCommands,
      aiTutorAutoOpenCount,
      miniPlayerPaywallTestFeature,
      showSearchChannelVideoPlayer,
      showUserHistory,
      enableMathMLKeyboard,
      isBundleSubscriber,
      isChannelsSubscriber,
      enableBundleChannelMiniPlayer,
      ChannelsVideoScore,
      promptShownCount,
      starterPromptLimit,
      hideChannelsPopout,
      showTranslate,
      translationLanguage,
      showRecommendedPrompts,
      showSourceReferencePage,
      AIEnableMarkdown,
      AIEnableImageContext,
      isSearchStudyAutoSelected,
      showSummary,
      showPractice,
      enableNewMathConfig,
      enableAISearch,
      isAIContentMediaIcon,
      aiStudyToolNameABTest,
      AIStudyToolReminder,
      showChannelsSubscribersStickyBanner,
      AIEnableNewValidateAnswer,
      channelRecommendationRedirectionABTest,
      searchChannelRecommendedMessage,
      processMarkDownFirst,
      shareChatbotReference,
      glossaryABCTest,
      AIEnableHigherOrderSuggestion,
      AIEnableQuizRandomization,
      enableChannelsRecommendations,
      searchChannelRecommendationDelay,
      quizQuestionLimit,
      miniplayerNoMatchingAssetsABTest,
      showRedirectionOverlay,
      enableAgenticAI,
      agenticGraphConfig,
      searchOverviewGraphConfig,
      enableAgenticSearchAIOverview,
      enableRTAudioTranslation,
      audioTranslationLanguage,
      audioLanguage,
      AIEnableQuizOptionShuffle,
      isAETEnabled: self.isAETEnabled,
      showQuizProgress,
      enableAET,
      assignmentCompletionPopupABTest,
      showAeTAssignmentsABCTest,
      enableUnassignedAETForInstructor,
      AIEnableDynamicSummary,
      AIEnableDynamicQuiz,
      AIShowFooterFAQLink
    };
  },
  getCoverThumbnailUrl() {
    return self.coverThumbnailUrl && `${env.CONTENT_HOST}${self.coverThumbnailUrl}`;
  },
  getPrimaryChannelId() {
    let channelId = null;

    if (self.channel_id
      && (CommonUtils.localStringCompare(
        self.channel_mapping_type, constants.CHANNEL_MAPPING_TYPES.EXACT 
      ) || CommonUtils.localStringCompare(
        self.channel_mapping_type, constants.CHANNEL_MAPPING_TYPES.EXACTLITE))) {
      channelId = self.channel_id;
    }

    return channelId;
  },
  getBookInfo(printPageNumber) {
    const businessModelCode = self.getBusinessModelCode();
    const {
      legacyBookId: bookId,
      legacyProductId: productId,
      channel_name,
      model
    } = self;
    const channelId = this.getPrimaryChannelId();
    const legacyIds = { bookId, productId };
    const legacyBookId = ProductUtils.getlegacyBookId(businessModelCode, legacyIds);
    return {
      author: self.authors,
      title: self.title,
      title2: self.title,
      thumbnailUrl: self.thumbnailUrl || constants.DEFAULT_BOOK_COVER,
      printPageNumber,
      isDoublePage: false,
      layoutDimensions: {
        rightStart: (self.twoPageLayoutLeft === constants.ODD)
      },
      preferenceObject: {
        sliderVal: '130',
        theme: 'White',
        zoom: 1.3
      },
      legacyBookId,
      productModel: model,
      channelId,
      channelName: channel_name,
      channelMappingType: self.channel_mapping_type

    };
  },
  getBookFeatures() {
    return self.configuration.ipadFeatures;
  },
  /**
   * Method to get business model code
   *
   */
  getBusinessModelCode() {
    return self.subscription.businessModelCode;
  },
  getMenuData() {
    const {
      configuration,
      format,
      model,
      platform,
      subscription,
      thumbnailUrl
    } = self;

    return {
      configuration,
      format,
      model,
      platform,
      subscription,
      thumbnailUrl
    };
  },
  getPdfLocale() {
    return PathUtils.getPdfLanguageCode(self.configuration.availableBookLanguages[0].languageID);
  },
  isPxe() {
    return (self.model === 'ETEXT2_PXE') || false;
  },
  isPdf() {
    return self.model === constants.ETEXT_PDF
     || this.getServerSideStatus()
     || self.model === constants.CUSTOM_COLLECTIONS;
  },

  /**
   * Method to get the server side status check
   *
   */
  getServerSideStatus() {
    const isServerEnabled = PathUtils.getQueryParameterbyName('isServerEnabled');

    if (!isServerEnabled) {
      return self.serversideEnabled && self.serversideProcessed;
    }
    if (isServerEnabled === 'true') {
      return self.serversideProcessed;
    }

    return false;
  },

  /**
   * Method to check audio flag
   *
   * @param {string} audioFlag
   */
  isAudioBook(audioFlag) {
    return CommonUtils.isAudioBook(audioFlag);
  },

  /**
   * Method to check whether it is a candian book
   *
   * @param {string} bookId
   */
  isCanadaBook(bookId) {
    return canadaBookList.indexOf(bookId) >= 0;
  },

  /**
   * Method to check audio beta flag
   *
   * @param {string} audioFlag
   */
  isAudioBetaBook(audioFlag) {
    const {
      AUDIO_FLAG_ON_BETA
    } = constants;

    return audioFlag === AUDIO_FLAG_ON_BETA;
  },

  /**
   * Method to get product model
   *
   * @param {Object} menuData
   */
  getProductModel(menuData) {
    const requestProductModel = true;
    let productModel = ProductUtils.getProductDescription(menuData, requestProductModel);

    productModel = productModel.split(' ').join('').toLowerCase();

    return productModel;
  },
  getResetState() {
    return {
      audioBook: '',
      authors: null,
      basePath: null,
      backLinkUrl: null,
      bookID: null,
      catalogUrl: null,
      configuration: {
        enableUserPrinting: false,
        studyTools: false,
        printOfferUrl: '',
        includeMathMLLib: false,
        headerFeatures: {
          hasPrintLink: false
        },
        generalFeatures: {
          hasLeftAccordion: false
        },
        ipadFeatures: {
          hotSpotColor: '',
          isUnderLineHotspot: false,
          regionHotSpotAlpha: '',
          underLineHotSpotColor: '',
          underLineHotSpotThickness: '',
          underLineHotSpotHoverColor: ''
        },
        availableBookLanguages: [{
          languageDirection: '',
          languageID: 1,
          languageNameKey: ''
        }]
      },
      coverThumbnailUrl: null,
      format: null,
      id: '',
      indexId: '',
      mathmlOn: false,
      mobileReady: false,
      model: null,
      platform: null,
      role: 'student',
      read_along: false,
      status: null,
      thumbnailUrl: null,
      title: null,
      version: null,
      subscription: {
        allowAccess: false,
        businessModelCode: '',
        entitlementTier: null,
        entitlementLevel: null,
        endDate: 0,
        startDate: 0,
        startDateUTC: null,
        status: null,
        type: '',
        subscriptionId: ''
      },
      tocUrl: null,
      serversideProcessed: null,
      serversideEnabled: null,
      serverSideUuid: null,
      twoPageLayoutLeft: null,
      legacyBookId: null,
      legacyProductId: null,
      channel_name: null,
      channel_id: null,
      channel_mapping_type: null,
      hasAccessToExactOrLiteMatch: false,
      secondary_channels: null,
      insitu_videos: null,
      isPPlusProduct: false,
      isAITutorSupported: null,
      isAIChannelTopicsSupported: null,
      isAETEnabled: false,
      isDynamicQuizSupported: false,
      isDynamicSummarySupported: false
    };
  }
})).actions(self => ({
  fetch(id, bundleId) {
    if (self.id === id) {
      Framework.getEventManager().publish(constants.PRODUCT_DATA_FETCHED, {});
    } else {
      Framework.getEventManager().publish(constants.PRODUCT_SYNC_REQUESTED, { id, bundleId });
    }
  },
  /**
   * Does subscription check
   *
   * @param {string} title
   * @param {string} userId
   * @param {string} productId
   * @param {string} courseId
   * @param {string} courseName
   */
  doSubscriptionCheck(
    title,
    userId,
    productId = '',
    courseId = '',
    courseName = '',
    match = ''
  ) {
    const isLMS = CommonUtils.isLMSCourse();
    if (!self.subscription.allowAccess) {
      CommonUtils.initializeGTM();
      const registrationUri = self.subscription.registrationUri || '';

      EventProvider.getEventManager().dispatch({
        event: 'Subscription Error',
        category: 'errorPage',
        action: 'Error',
        data: {
          title,
          userId,
          productId,
          courseId,
          courseName
        }
      });
      // console page should not be opened for LMS flow
      if (registrationUri.startsWith(env.ZEPPELIN_URL) && !isLMS) {
        // redirecting to console link for renew option
        window.location = `${env.CONSOLE_URL}`;
      } else {
        // getting match props will be challenging here, hence going with event approach
        if (isLMS) {
          Framework.getEventManager().publish(constants.LMS_PDP_REDIRECT_EVENT, {});
          return;
        }
        const errorCode = registrationUri ? 'SUBSCRIPTION_REGISTRATION' : 'SUBSCRIPTION_GENERIC';
        Framework.redirectToErrorPage(errorCode, 'common.PURCHASE', registrationUri);
      }
    }
  },
  /**
   * Sends error events to GA
   *
   * @param {Object} data
   * @param {string} productId
   * @param {string} courseId
   */
  sendErrorEvents(data, productId = '', courseId = '', isLMS = false, match = {}, flags = {}) {
    const {
      errorDetails: {
        error
      } = {}
    } = data;
    CommonUtils.initializeGTM();
    if(isLMS){
      CommonUtils.handleLMSFlow(match, window.location.href, flags);
    }
    EventProvider.getEventManager().dispatch({
      event: 'Error',
      category: 'Error',
      action: 'Error logs',
      data: {
        errorMessage: error ? error.message : '',
        errorCode: error ? error.status : '',
        apiDetail: data.api,
        productId,
        courseId
      }
    });
  },
  /**
   * Updates data Layer based on response
   *
   */
  updateDataLayer() {
    const customFields = {
      businessModelCode: self.subscription.businessModelCode || '',
      productModelName: ProductUtils.getProductDescription(self.getMenuData()) || '',
      userId: Framework.getStoreRegistry().getStore('user').id
    };
    CommonUtils.dispatchGaEvent(null, null, null, null, customFields);
  },
  getContentType(isFromCourse) {
    let product = Framework.getStoreRegistry().getStore('product');
    if (isFromCourse) {
      product = Framework.getStoreRegistry().getStore('course').getProduct();
    }
    return product.format;
  },
  sendPageUnloadEvent(course, previousPageId, isFromCourse, durationSeconds, UnLoadUuid, UnLoadDt, UnloadUserLocalLoadDt, subscriptionData, currentPageId) {
    const courseInfo = isFromCourse ? course.products[0] : course;
    const {
      id, platform, model, title, role
    } = courseInfo;

    const contentType = self.getContentType(isFromCourse);
    const courseIdType = self.platform === constants.BUNDLED_PLATFORM ? constants.COURSE_TYPE_SMS : constants.COURSE_TYPE_REGISTRAR;
    const currrentPageUrn = window.location.href.split('pages/');
    const pageUserNavigatedFromUrn = previousPageId ? `${currrentPageUrn[0]}pages/${previousPageId}` : '';
    const pageUserNavigatedToUrn = currentPageId ? `${currrentPageUrn[0]}pages/${currentPageId}` : '';
    const pageId = previousPageId;
    const unLoadEvent = {
      event: constants.TELEMETRY_PAGE_UNLOAD,
      product_id: id,
      product_platform_code: platform,
      product_model_name: model || constants.PRODUCT_MODEL,
      business_model_code: self.subscription.businessModelCode,
      book_id: courseInfo.bookID || courseInfo.id,
      pearson_subscription_id: (subscriptionData && subscriptionData.subscriptionId) || null,
      pearson_subscription_tier_code: (subscriptionData && subscriptionData.entitlementLevel) || null,
      course_id: isFromCourse ? course.id : null,
      course_id_type: isFromCourse ? courseIdType : null,
      course_section_id: isFromCourse ? course.id : null, // By Telemetry team expectation course_id and  course_section_id will be the same
      course_section_id_type: isFromCourse ? courseIdType : null,
      course_section_title: courseInfo.title,
      content_id: pageId,
      content_id_type: env.PRODUCT.toLowerCase(),
      content_type: constants.CONTENT_TYPE, // It's static , Will change once will get data
      content_title: title,
      discipline_id: null,
      discipline_name: null,
      load_dt: UnLoadDt, // Timestamp in ISO Format: 2020-04-15T12:33:45.237Z
      user_local_load_dt: UnloadUserLocalLoadDt, // "2021-05-04T17:38:48:195"
      time_on_task_uuid: UnLoadUuid,
      message_id: CommonUtils.getUUID(),
      transaction_dt: new Date().toISOString(), // Timestamp in ISO Format: 2020-04-15T12:33:45.237Z
      transaction_local_dt: CommonUtils.getLocalIsoTime(), // "2021-05-04T17:38:48:195"
      unload_caused_by_code: courseInfo.role || role,
      duration_in_seconds: parseInt(durationSeconds, 10),
      page_urn: (window.location.href).split('?')[0],
      userAgent: window.navigator.userAgent
    };
    const request = {
      type: constants.USER_UNLOADS_CONTENT,
      pageId,
      userId: Framework.getStoreRegistry().getStore('user').id,
      isBundled: self.platform === constants.BUNDLED_PLATFORM,
      isFromCourse,
      businessModelCode: self.subscription.businessModelCode,
      playerModeCode: constants.PLAYER_MODE_CODE, // Static Will change once will get data, Ex: reading vs listening
      productId: id,
      pearsonSubscriptionId: (subscriptionData && subscriptionData.subscriptionId) || null,
      pearsonSubscriptionTierCode: (subscriptionData && subscriptionData.entitlementLevel) || null,
      loadUuid: UnLoadUuid,
      loadDt: UnLoadDt,
      userLocalDt: UnloadUserLocalLoadDt,
      bookId: courseInfo.bookID || courseInfo.id,
      durationInSeconds: parseInt(durationSeconds, 10),
      courseSectionTitle: isFromCourse && courseInfo.title,
      pageTitle: CommonUtils.getPageName(pageId),
      productModelName: model,
      productPlatformCode: platform,
      contentType,
      contentTitle: title,
      pageUserNavigatedFromUrn,
      pageUserNavigatedToUrn
    };
    if (isFromCourse) {
      request.course = Framework.getStoreRegistry().getStore('course');
    } else {
      request.product = getSnapshot(self);
    }
    /* AS per Naga instruction for testing load and Unloadevent, removed PDF restriction */
    Framework.getEventManager().publish(constants.AUTOBAHN_TIMEONTASK, request);
    CommonUtils.initializeGTM();
    EventProvider.getEventManager().dispatch(unLoadEvent);
  },
  sendPageLoadEvent(course, pageId, isFromCourse, loadUuid, loadDt, userLocalLoadDt, subscriptionData, previousPageId, navData) {
    const courseInfo = isFromCourse ? course.products[0] : course;
    const {
      id, platform, model, title
    } = courseInfo;
    const contentType = self.getContentType(isFromCourse);
    const businessModelCode = self.subscription.businessModelCode;
    const courseIdType = self.platform === constants.BUNDLED_PLATFORM ? constants.COURSE_TYPE_SMS : constants.COURSE_TYPE_REGISTRAR;
    const currrentPageUrn = window.location.href.split('pages/');
    const pageUserNavigatedFromUrn = previousPageId ? `${currrentPageUrn[0]}pages/${previousPageId}` : '';
    const pageUserNavigatedToUrn = `${currrentPageUrn[0]}pages/${pageId}`;

    const loadEvent = {
      event: constants.TELEMETRY_PAGE_LOAD,
      page_name: CommonUtils.getPageName(pageId),
      product_id: id,
      product_platform_code: platform,
      product_model_name: model || constants.PRODUCT_MODEL,
      business_model_code: businessModelCode,
      book_id: courseInfo.bookID || courseInfo.id,
      pearson_subscription_id: (subscriptionData && subscriptionData.subscriptionId) || null,
      pearson_subscription_tier_code: (subscriptionData && subscriptionData.entitlementLevel) || null,
      course_id: isFromCourse ? course.id : null,
      course_id_type: isFromCourse ? courseIdType : null,
      course_section_id: isFromCourse ? course.id : null, // By Telemetry team expectation course_id and  course_section_id will be the same
      course_section_id_type: isFromCourse ? courseIdType : null,
      course_section_title: courseInfo.title,
      content_id: pageId,
      content_id_type: env.PRODUCT.toLowerCase(),
      content_type: constants.CONTENT_TYPE, // It's static , Will change once will get the data
      content_title: title,
      discipline_id: null,
      discipline_name: null,
      load_dt: loadDt, // Timestamp in ISO Format: 2020-04-15T12:33:45.237Z
      user_local_load_dt: userLocalLoadDt, // "2021-05-04T17:38:48:195"
      time_on_task_uuid: loadUuid,
      message_id: CommonUtils.getUUID(),
      transaction_dt: new Date().toISOString(), // Timestamp in ISO Format: 2020-04-15T12:33:45.237Z
      transaction_local_dt: CommonUtils.getLocalIsoTime(), // "2021-05-04T17:38:48:195"
      capabilities_platform_code: null,
      app_component_version_id: null,
      offer_id: null,
      entitlement_id: (subscriptionData && subscriptionData.entitlementLevel) || null,
      page_urn: (window.location.href).split('?')[0],
      chapter_id: navData ? navData.chapterId : null,
      userAgent: window.navigator.userAgent
    };

    const request = {
      type: constants.USER_LOADS_CONTENT,
      pageId,
      userId: Framework.getStoreRegistry().getStore('user').id,
      isBundled: self.platform === constants.BUNDLED_PLATFORM,
      isFromCourse,
      businessModelCode: self.subscription.businessModelCode,
      playerModeCode: constants.PLAYER_MODE_CODE, // Static Will change once will get data, Ex: reading vs listening
      productId: id,
      pearsonSubscriptionId: (subscriptionData && subscriptionData.subscriptionId) || null,
      pearsonSubscriptionTierCode: (subscriptionData && subscriptionData.entitlementLevel) || null,
      loadUuid,
      loadDt,
      userLocalDt: userLocalLoadDt,
      bookId: courseInfo.bookID || courseInfo.id,
      courseSectionTitle: isFromCourse && courseInfo.title,
      pageTitle: CommonUtils.getPageName(pageId),
      productModelName: model,
      productPlatformCode: platform,
      contentType,
      contentTitle: title,
      pageUserNavigatedFromUrn,
      pageUserNavigatedToUrn
    };
    if (isFromCourse) {
      request.course = Framework.getStoreRegistry().getStore('course');
    } else {
      request.product = getSnapshot(self);
    }
    /* AS per Naga instruction for testing load and Unloadevent, removed PDF restriction */
    Framework.getEventManager().publish(constants.AUTOBAHN_TIMEONTASK, request);
    CommonUtils.initializeGTM();
    EventProvider.getEventManager().dispatch(loadEvent);
  },
  sendPageNavigation(courseInfo, pageId, isFromCourse, navData) {
    const { id } = courseInfo;
    const eventAction = navData && navData.action ? `${navData.action.charAt(0).toUpperCase()}${navData.action.slice(1)}` : null;
    const navigationEvent = {
      event: constants.NAVIGATION,
      event_action: eventAction,
      event_category: 'Content Navigation',
      category: 'Content Navigation',
      event_label: eventAction,
      event_value: pageId,
      transaction_local_dt: CommonUtils.getLocalIsoTime(),
      user_local_event_dt: CommonUtils.getLocalIsoTime(),
      message_id: CommonUtils.getUUID(),
      event_unix_timestamp: new Date().getTime(),
      chapter_id: navData ? navData.chapterId : null,
      content_id: pageId || null
    };
    if (isFromCourse) {
      navigationEvent.course_section_id = id;
    }
    CommonUtils.initializeGTM();
    EventProvider.getEventManager().dispatch(navigationEvent);
  },
  set(product) {
    applySnapshot(self, product);
    const userId = Framework.getStoreRegistry().getStore('user').id;

    self.doSubscriptionCheck(self.title, userId, self.id);
    self.updateDataLayer();
    Framework.getEventManager().publish(constants.PRODUCT_DATA_FETCHED, {});
  },
  /**
   * Function to load the PLA events
   * @param {string} pageId - page Id
   * @param {Boolean} isFromCourse - true if it is from courseware
   * @param {string} previousPageId - previous page Id
   * @param {number} durationSeconds - Duration between load and unload
   * @param {string} loadUuid - Unique ID
   * @param {string} loadDt - ISO String
   * @param {string} userLocalLoadDt - ISO String
   * @param {string} UnLoadUuid - Unique ID
   * @param {string} UnLoadDt - ISO String
   * @param {string} UnloadUserLocalLoadDt - ISO String
   */
  setPLAEvents(product, pageId, isFromCourse, previousPageId, durationSeconds, loadUuid, loadDt, userLocalLoadDt, UnLoadUuid, UnLoadDt, UnloadUserLocalLoadDt, loadType, navData) {
    const gpsSubscriptionOfProduct = self.getGpsSubscriptionOfProduct();
    const { status } = gpsSubscriptionOfProduct;
    const subscriptionData = (status === constants.ACTIVE) ? gpsSubscriptionOfProduct : null;
    if (previousPageId && previousPageId !== pageId && loadType === constants.UNLOAD) {
      self.sendPageUnloadEvent(product, previousPageId, isFromCourse, durationSeconds, UnLoadUuid, UnLoadDt, UnloadUserLocalLoadDt, subscriptionData, pageId);
      self.sendPageNavigation(product, pageId, isFromCourse, navData); // For Navigation tracking purpose
    }
    if (pageId && pageId !== previousPageId && loadType === constants.LOAD) {
      self.sendPageLoadEvent(product, pageId, isFromCourse, loadUuid, loadDt, userLocalLoadDt, subscriptionData, previousPageId, navData);
      CommonUtils.initTelemetryPageAction();
    }
  },
  /**
   * Get The product's GPS subscription
   *
   * @returns
   */
  getGpsSubscriptionOfProduct() {
    return self.subscription;
  },
  /**
   * set The product's matching GPS subscription based on the subscriptionId
   *
   */
  setGpsSubscriptionOfProduct() {
    const subscription = self.subscription;
    const subscriptionIdOfProduct = subscription && subscription.subscriptionId;
    const userStore = Framework.getStoreRegistry().getStore('user');
    const { startDate = null, entitlementTier = null, entitlementLevel = null, status = null } = CommonUtils.getMatchingGpsSubscriptionOfBook(
      subscriptionIdOfProduct,
      getSnapshot(userStore)
    );
    // eslint-disable-next-line no-param-reassign
    self.subscription = {
      ...subscription,
      startDateUTC: startDate,
      entitlementTier,
      entitlementLevel,
      status
    };
  },
  /**
   * Determines and sets whether or not it is a PPlus product and setting subscription of book.
   *
   */
  setIsPPlusProductAndGpsSubscriptionOfProduct() {
    self.setGpsSubscriptionOfProduct();
    const isPPlusProduct = CommonUtils.isPPlusUser(self.subscription, constants.EREADER);

    // eslint-disable-next-line no-param-reassign
    self.isPPlusProduct = isPPlusProduct;
  },
  /**
   * Returns whether or not it is a PPlus product
   *
   * @returns
   */
  getIsPPlusProduct() {
    return self.isPPlusProduct;
  },
  /**
   * Set channels access on the product from Marin /channels/access response.
   *
   * @param {Object} channelsAccessData - Channels access response data
   * @param {Object} [options={}]
   * @param {String} [options.courseId] - Course id for mapping type computation
   * @returns {void}
   */
  setChannelsAccess(channelsAccessData = {}, options = {}) {
    const { bookId, ...channelsAccess } = channelsAccessData;
    const {
      channel,
      status,
      matchType
    } = channelsAccess;
    const { courseId = null } = options;

    if (bookId && self.id !== bookId && `${self.bookID}` !== `${bookId}`) {
      return;
    }

    const isMlm = CommonUtils.isIntegratedBMC(self.getBusinessModelCode());
    const mappedAccess = CommonUtils.mapChannelsAccess({
      channel,
      status,
      matchType,
      courseId,
      isMlm
    });

    // eslint-disable-next-line no-param-reassign
    self.channel_id = mappedAccess.channel_id;
    // eslint-disable-next-line no-param-reassign
    self.channel_mapping_type = mappedAccess.channel_mapping_type;
    // eslint-disable-next-line no-param-reassign
    self.channel_name = mappedAccess.channel_name;
    // eslint-disable-next-line no-param-reassign
    self.hasAccessToExactOrLiteMatch = mappedAccess.hasAccessToExactOrLiteMatch;
  },
  /**
   * Clear channels access fields on the product.
   *
   * @returns {void}
   */
  removeChannelAccess() {
    // eslint-disable-next-line no-param-reassign
    self.channel_id = null;
    // eslint-disable-next-line no-param-reassign
    self.channel_mapping_type = null;
    // eslint-disable-next-line no-param-reassign
    self.channel_name = null;
    // eslint-disable-next-line no-param-reassign
    self.hasAccessToExactOrLiteMatch = false;
  },
  /**
   * Fetches channels access for a book and updates the product when the request settles.
   *
   * @param {string} bookId - Book ID
   * @param {Object} [options={}]
   * @param {String} [options.courseId] - Course id for mapping type computation
   * @param {Function} [onComplete] - Called on success, error, or when bookId is missing
   * @returns {void}
   */
  fetchChannelsAccess(bookId, options = {}, onComplete) {
    const onChannelsAccessFetched = (channelsAccessData, error) => {
      if (error || !channelsAccessData?.channel) {
        self.removeChannelAccess();
      } else {
        self.setChannelsAccess(channelsAccessData, options);
      }

      if (typeof onComplete === 'function') {
        onComplete(channelsAccessData, error);
      }
    };

    CommonUtils.requestChannelsAccess(bookId, onChannelsAccessFetched);
  },
  reset() {
    applySnapshot(self, self.getResetState());
  }
}));
export default Product;
